import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Heading,
  Select,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import { ENDPOINTS } from "utils/Constants";
import { currentUser } from "utils/CurrentUser";
import { notify } from "components/ToastNotification";
import { useI18n } from "i18n";

export default function Challenges() {
  const { t, language } = useI18n();
  const apiLanguageByUi = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    it: 'Italian',
    zh: 'Chinese',
    ar: 'Arabic',
    ru: 'Russian',
    ja: 'Japanese',
    hi: 'Hindi',
    sq: 'Albanian',
  };
  const [overview, setOverview] = useState(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [skillId, setSkillId] = useState("");
  const [skillLevel, setSkillLevel] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(8);

  const [challenge, setChallenge] = useState(null);
  const [submissionText, setSubmissionText] = useState("");
  const [evaluation, setEvaluation] = useState(null);

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const accentBg = useColorModeValue("orange.50", "orange.900");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const heroBg = useColorModeValue(
    "linear-gradient(110deg, #F59E0B 0%, #EA580C 45%, #9A3412 100%)",
    "linear-gradient(110deg, #F59E0B 0%, #C2410C 45%, #7C2D12 100%)"
  );

  const fetchOverview = async () => {
    setLoadingOverview(true);
    try {
      const response = await axios.get(ENDPOINTS.GET_CHALLENGE_OVERVIEW, {
        headers: { Authorization: `Bearer ${currentUser.token}` },
      });
      if (!response?.data?.success) {
        notify(response?.data?.output || "Could not fetch challenge overview", "error");
        return;
      }
      setOverview(response.data.data);
      setUpgradeRequired(false);
      if (!skillId && response.data.data.learningSkills?.length > 0) {
        setSkillId(String(response.data.data.learningSkills[0].id));
        setSkillLevel(String(response.data.data.learningSkills[0].currentLevel || ""));
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        setUpgradeRequired(true);
      } else {
        notify("Could not fetch challenge overview", "error");
      }
    } finally {
      setLoadingOverview(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateChallenge = async () => {
    setLoadingGenerate(true);
    try {
      const response = await axios.post(
        ENDPOINTS.GENERATE_CHALLENGE,
        {
          skillId: skillId ? Number(skillId) : null,
          currentLevel: skillLevel || null,
          durationMinutes,
          language: apiLanguageByUi[language] || 'English',
        },
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      );

      if (!response?.data?.success) {
        notify(response?.data?.output || "Failed to generate challenge", "error");
        return;
      }

      setChallenge(response.data.data);
      setSubmissionText("");
      setEvaluation(null);
      notify("Challenge generated", "success");
    } catch (error) {
      notify(error?.response?.data?.output || "Failed to generate challenge", "error");
    } finally {
      setLoadingGenerate(false);
    }
  };

  const submitChallenge = async () => {
    if (!challenge?.id || submissionText.trim().length < 20) {
      notify("Please add a meaningful submission (at least 20 chars).", "error");
      return;
    }

    setLoadingSubmit(true);
    try {
      const response = await axios.post(
        ENDPOINTS.SUBMIT_CHALLENGE,
        {
          challengeId: challenge.id,
          submissionText,
          language: apiLanguageByUi[language] || 'English',
        },
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      );

      if (!response?.data?.success) {
        notify(response?.data?.output || "Submission failed", "error");
        return;
      }

      setEvaluation(response.data.data);
      notify("Challenge evaluated", "success");
      fetchOverview();
    } catch (error) {
      notify(error?.response?.data?.output || "Submission failed", "error");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const stats = useMemo(() => overview?.stats || {}, [overview]);
  const recentSubmissions = useMemo(() => overview?.recentSubmissions || [], [overview]);
  const learningSkills = useMemo(() => overview?.learningSkills || [], [overview]);
  const formatLevel = (level) => level ? `${level.charAt(0).toUpperCase()}${level.slice(1)}` : "";

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px="20px" bg={pageBg}>
      <Box bg={heroBg} color="white" borderRadius="20px" p={{ base: "20px", md: "28px" }} boxShadow="xl" mb="20px">
        <Text textTransform="uppercase" fontSize="sm" letterSpacing="1px" opacity={0.9}>{t("challenges.newActivity", "New Activity")}</Text>
        <Heading mt="2" fontSize={{ base: "2xl", md: "3xl" }}>{t("challenges.title", "Challenge Mode")}</Heading>
        <Text mt="3" maxW="800px" opacity={0.95}>
          {t("challenges.subtitle", "Real-life scenarios, clear rubric, AI evaluation, and rewards. This proves ability beyond quizzes.")}
        </Text>
      </Box>

      {upgradeRequired ? (
        <Box bg={cardBg} borderRadius="16px" p="24px" boxShadow="md">
          <Heading fontSize="2xl">{t("common.upgradeRequired", "Upgrade Required")}</Heading>
          <Text mt="2" color={mutedText}>
            {t("common.standardPremiumOnly", "This feature is available only on Standard and Premium plans.")}
          </Text>
          <Button mt="5" variant="darkBrand" onClick={() => (window.location.href = "/admin/profile")}>{t("common.viewPlans", "View Plans")}</Button>
        </Box>
      ) : (
        <Grid templateColumns={{ base: "1fr", xl: "1.2fr 1fr" }} gap="20px">
          <Box bg={cardBg} borderRadius="16px" p="20px" boxShadow="md">
            <Heading fontSize="2xl">{t("challenges.create", "Create Challenge")}</Heading>
            <Stack spacing="12px" mt="4">
              <Box>
                <Text fontSize="sm" mb="6px">{t("challenges.skill", "Skill")}</Text>
                <Select value={skillId} onChange={(event) => {
                  const selectedId = event.target.value;
                  setSkillId(selectedId);
                  const selectedSkill = learningSkills.find((skill) => String(skill.id) === String(selectedId));
                  setSkillLevel(selectedSkill?.currentLevel || "");
                }}>
                  <option value="">Auto-select</option>
                  {learningSkills.map((skill) => (
                    <option value={skill.id} key={`${skill.id}-${skill.currentLevel}`}>
                      {skill.name} {skill.currentLevel ? `(${formatLevel(skill.currentLevel)})` : ""}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="sm" mb="6px">{t("challenges.duration", "Duration")}</Text>
                <Select value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.target.value))}>
                  {[5, 6, 7, 8, 9, 10, 11, 12].map((minutes) => (
                    <option value={minutes} key={minutes}>{minutes} min</option>
                  ))}
                </Select>
              </Box>
              <Button onClick={generateChallenge} isLoading={loadingGenerate} variant="darkBrand">
                {t("challenges.generate", "Generate Challenge")}
              </Button>
            </Stack>

            {challenge && (
              <Box mt="20px" p="16px" borderRadius="12px" bg={accentBg}>
                <Flex justify="space-between" align="center" wrap="wrap" gap="8px">
                  <Heading fontSize="xl">{challenge.title}</Heading>
                  <HStack spacing="8px">
                    <Badge colorScheme="orange">{challenge.difficulty}</Badge>
                    <Badge colorScheme="purple">{challenge.estimatedMinutes} min</Badge>
                  </HStack>
                </Flex>
                <Text mt="3">{challenge.scenarioPrompt}</Text>

                <Text mt="4" fontWeight="700">Rubric</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap="10px" mt="2">
                  {(challenge.rubric || []).map((item) => (
                    <Box key={`${item.criterion}-${item.maxScore}`} p="10px" borderRadius="10px" bg={cardBg}>
                      <Text fontWeight="700">{item.criterion} ({item.maxScore})</Text>
                      <Text fontSize="sm" color={mutedText}>{item.whatGoodLooksLike}</Text>
                    </Box>
                  ))}
                </SimpleGrid>

                <Text mt="4" fontWeight="700">{t("challenges.yourSubmission", "Your Submission (text for now)")}</Text>
                <Textarea
                  mt="2"
                  value={submissionText}
                  onChange={(event) => setSubmissionText(event.target.value)}
                  placeholder="Write your challenge response..."
                  minH="140px"
                  bg={cardBg}
                />
                <Button mt="3" onClick={submitChallenge} isLoading={loadingSubmit} colorScheme="orange">
                  {t("challenges.submit", "Submit For AI Evaluation")}
                </Button>
              </Box>
            )}
          </Box>

          <Stack spacing="20px">
            <Box bg={cardBg} borderRadius="16px" p="20px" boxShadow="md">
              <Heading fontSize="xl">{t("challenges.rewards", "Rewards")}</Heading>
              <SimpleGrid columns={2} mt="4" gap="12px">
                <Stat bg={accentBg} p="12px" borderRadius="12px">
                  <StatLabel>Total XP</StatLabel>
                  <StatNumber>{stats.totalXp || 0}</StatNumber>
                </Stat>
                <Stat bg={accentBg} p="12px" borderRadius="12px">
                  <StatLabel>Current Streak</StatLabel>
                  <StatNumber>{stats.currentStreak || 0}</StatNumber>
                </Stat>
                <Stat bg={accentBg} p="12px" borderRadius="12px">
                  <StatLabel>Best Streak</StatLabel>
                  <StatNumber>{stats.bestStreak || 0}</StatNumber>
                </Stat>
                <Stat bg={accentBg} p="12px" borderRadius="12px">
                  <StatLabel>Unlocks</StatLabel>
                  <StatNumber>{(stats.unlocks || []).length}</StatNumber>
                </Stat>
              </SimpleGrid>
              {(stats.unlocks || []).length > 0 && (
                <Flex mt="3" wrap="wrap" gap="8px">
                  {stats.unlocks.map((item) => (
                    <Badge key={item} colorScheme="green">{item}</Badge>
                  ))}
                </Flex>
              )}
            </Box>

            {evaluation && (
              <Box bg={cardBg} borderRadius="16px" p="20px" boxShadow="md">
                <Heading fontSize="xl">{t("challenges.aiEvaluation", "AI Evaluation")}</Heading>
                <Text mt="2"><b>Score:</b> {evaluation.evaluation?.overallScore || 0}/100</Text>
                <Text mt="1"><b>XP Earned:</b> {evaluation.rewards?.xpEarned || 0}</Text>
                <Text mt="1"><b>Streak +:</b> {evaluation.rewards?.streakAwarded || 0}</Text>
                {evaluation.rewards?.unlockedReward && (
                  <Text mt="1"><b>Unlocked:</b> {evaluation.rewards.unlockedReward}</Text>
                )}

                <Text mt="4" fontWeight="700">Actionable Feedback</Text>
                <Stack spacing="8px" mt="2">
                  {(evaluation.evaluation?.actionableFeedback || []).map((line) => (
                    <Box key={line} p="10px" borderRadius="10px" bg={accentBg}>
                      <Text fontSize="sm">{line}</Text>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            <Box bg={cardBg} borderRadius="16px" p="20px" boxShadow="md">
              <Heading fontSize="xl">{t("challenges.recentAttempts", "Recent Attempts")}</Heading>
              <Stack mt="3" spacing="10px">
                {recentSubmissions.length === 0 ? (
                  <Text color={mutedText}>{loadingOverview ? "Loading..." : "No challenge submissions yet."}</Text>
                ) : recentSubmissions.map((item) => (
                  <Box key={item.id} p="10px" borderRadius="10px" bg={accentBg}>
                    <Text fontWeight="700">{item.challengeTitle}</Text>
                    <Text fontSize="sm">Score: {item.score} | XP: {item.xpEarned}</Text>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Grid>
      )}
    </Box>
  );
}
